/**
 * FP3-075B — Simple-Mode friendly "outside active schedule period"
 * card. Replaces the terse "Editing is disabled outside the active
 * schedule period" dead-end for Simple-Mode users.
 *
 *   • If an active period exists →
 *       primary: "Go to active schedule period" (jump baseDate)
 *       secondary: "Edit schedule dates" (route to /schedule?tab=school-year)
 *   • If no active period exists yet →
 *       primary: "Set schedule dates" (route to /schedule?tab=school-year)
 *
 * Advanced Mode keeps the original terse copy untouched so power
 * users see no behavior change.
 */
import { useNavigate } from "react-router-dom";
import { CalendarBlank, ArrowRight, Wrench } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function OutsidePeriodCard({
  variant = "view", // "view" | "builder"
  activePeriod,
  onJumpToActivePeriod,
}) {
  const navigate = useNavigate();
  const hasPeriod = !!(activePeriod?.start_date && activePeriod?.end_date);

  const goToSchoolYear = () => {
    // Advanced Tools disclosure in the sidebar auto-expands when the
    // active tab is one of the advanced tabs (handled in Sidebar.js).
    navigate("/schedule?tab=school-year");
  };

  return (
    <div
      data-testid={`outside-period-${variant}-simple`}
      className="mx-auto max-w-xl py-12 text-center"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#E8F0EB] mb-4">
        <CalendarBlank size={22} weight="duotone" className="text-[#1E3F33]" />
      </div>
      {hasPeriod ? (
        <>
          <h2
            data-testid="outside-period-title"
            className="text-[18px] font-semibold text-[#1F2937] heading-font mb-1.5"
          >
            Your selected week is outside your active schedule dates.
          </h2>
          <p className="text-[13px] text-[#6B7280] leading-relaxed mb-5 max-w-md mx-auto">
            Frame Practice uses schedule dates to know when your weekly
            schedule should appear. You can jump to your active schedule
            period or update your schedule dates.
          </p>
          <p
            data-testid="outside-period-range"
            className="text-[11.5px] text-[#9B9890] mb-5"
          >
            {activePeriod.name} runs{" "}
            {new Date(activePeriod.start_date + "T12:00:00").toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", year: "numeric" }
            )}{" "}
            –{" "}
            {new Date(activePeriod.end_date + "T12:00:00").toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", year: "numeric" }
            )}
            .
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button
              type="button"
              data-testid="outside-period-jump-btn"
              onClick={() => onJumpToActivePeriod?.(activePeriod)}
              className="bg-[#1E3F33] hover:bg-[#173127] text-white gap-1.5"
            >
              Go to active schedule period
              <ArrowRight size={13} />
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="outside-period-edit-dates-btn"
              onClick={goToSchoolYear}
              className="gap-1.5 border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB]"
            >
              <Wrench size={13} weight="duotone" />
              Edit schedule dates
            </Button>
          </div>
        </>
      ) : (
        <>
          <h2
            data-testid="outside-period-title"
            className="text-[18px] font-semibold text-[#1F2937] heading-font mb-1.5"
          >
            Set your schedule dates to start building your calendar.
          </h2>
          <p className="text-[13px] text-[#6B7280] leading-relaxed mb-5 max-w-md mx-auto">
            Frame Practice uses schedule dates to know when your weekly
            schedule should appear. Set your active schedule period and
            you&rsquo;ll be ready to build.
          </p>
          <Button
            type="button"
            data-testid="outside-period-set-dates-btn"
            onClick={goToSchoolYear}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white gap-1.5"
          >
            Set schedule dates
            <ArrowRight size={13} />
          </Button>
        </>
      )}
    </div>
  );
}
